import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Input,
  Button,
  Badge,
  Empty,
  Spin,
  message,
  Image,
  Tabs,
  Table,
  Tag,
  Space,
  Modal,
  Select,
  InputNumber,
  Switch,
  Alert,
} from 'antd';
import {
  SendOutlined,
  PictureOutlined,
  LeftOutlined,
  PlusOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import {
  customerServiceApi,
  serverControlApi,
  AiPreviewResult,
  AiReplyLog,
  AiSupportConfig,
  KbDocument,
} from '../services/api';

const { TextArea } = Input;

const MOBILE_BREAKPOINT = 768;
const DEFAULT_AI_CONFIG: AiSupportConfig = {
  enabled: false,
  provider: 'openai',
  model: 'gpt-5.4',
  base_url: '',
  request_timeout: 30,
  low_risk_auto_reply_enabled: true,
  allowed_auto_reply_intents: [],
  force_handoff_keywords: [],
  api_key: '',
};
const DEFAULT_DOC_FORM = {
  type: 'qa' as 'qa' | 'article',
  title: '',
  question: '',
  answer: '',
  content: '',
  tags: '',
  priority: 0,
  is_active: true,
};
const INTENT_OPTIONS = [
  '入驻流程',
  '机器人创建/绑定',
  '支付通道配置说明',
  '商品/群组/会员配置说明',
  '标准FAQ',
];

interface Conversation {
  customer_telegram_id: number;
  customer_name: string;
  customer_username: string | null;
  last_message: string;
  last_time: string | null;
  unread_count: number;
}

interface Message {
  id: number;
  direction: 'in' | 'out';
  content: string;
  message_type: string;
  media_file_id: string | null;
  media_access_token?: string | null;
  created_at: string | null;
  customer_name: string | null;
  customer_username: string | null;
}

type KbFormState = typeof DEFAULT_DOC_FORM & {
  id?: number;
};

const CustomerService: React.FC = () => {
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [kbDocuments, setKbDocuments] = useState<KbDocument[]>([]);
  const [loadingKb, setLoadingKb] = useState(false);
  const [kbModalOpen, setKbModalOpen] = useState(false);
  const [kbSaving, setKbSaving] = useState(false);
  const [kbForm, setKbForm] = useState<KbFormState>(DEFAULT_DOC_FORM);
  const [aiConfig, setAiConfig] = useState<AiSupportConfig>(DEFAULT_AI_CONFIG);
  const [loadingAiConfig, setLoadingAiConfig] = useState(false);
  const [savingAiConfig, setSavingAiConfig] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<AiPreviewResult | null>(null);
  const [aiLogs, setAiLogs] = useState<AiReplyLog[]>([]);
  const [loadingAiLogs, setLoadingAiLogs] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const data = await customerServiceApi.getConversations();
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      // 静默
    }
  }, []);

  const fetchMessages = useCallback(async (customerId: number) => {
    try {
      const data = await customerServiceApi.getMessages(customerId, 200);
      setMessages(Array.isArray(data) ? data : []);
    } catch {
      // 静默
    }
  }, []);

  const fetchKbDocuments = useCallback(async () => {
    try {
      setLoadingKb(true);
      const data = await customerServiceApi.getKbDocuments();
      setKbDocuments(Array.isArray(data) ? data : []);
    } finally {
      setLoadingKb(false);
    }
  }, []);

  const fetchAiConfig = useCallback(async () => {
    try {
      setLoadingAiConfig(true);
      const data = await serverControlApi.getAiSupportConfig();
      setAiConfig({
        ...DEFAULT_AI_CONFIG,
        ...data,
        api_key: '',
      });
    } finally {
      setLoadingAiConfig(false);
    }
  }, []);

  const fetchAiLogs = useCallback(async (customerId?: number | null) => {
    try {
      setLoadingAiLogs(true);
      const data = await customerServiceApi.getAiLogs({
        customer_telegram_id: customerId || undefined,
        limit: 50,
      });
      setAiLogs(Array.isArray(data) ? data : []);
    } finally {
      setLoadingAiLogs(false);
    }
  }, []);

  useEffect(() => {
    setLoadingConv(true);
    fetchConversations().finally(() => setLoadingConv(false));
    fetchKbDocuments();
    fetchAiConfig();
    fetchAiLogs();
    const timer = setInterval(fetchConversations, 5000);
    return () => clearInterval(timer);
  }, [fetchConversations, fetchKbDocuments, fetchAiConfig, fetchAiLogs]);

  useEffect(() => {
    if (!selectedCustomer) return;
    setLoadingMsg(true);
    fetchMessages(selectedCustomer).finally(() => setLoadingMsg(false));
    const timer = setInterval(() => fetchMessages(selectedCustomer), 5000);
    return () => clearInterval(timer);
  }, [selectedCustomer, fetchMessages]);

  useEffect(() => {
    if (activeTab === 'strategy') {
      fetchAiLogs(selectedCustomer);
    }
  }, [activeTab, selectedCustomer, fetchAiLogs]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSelectConversation = (customerId: number) => {
    setSelectedCustomer(customerId);
    setMessages([]);
  };

  const handleBack = () => {
    setSelectedCustomer(null);
  };

  const handleSend = async () => {
    if (!inputText.trim() || !selectedCustomer) return;
    setSending(true);
    try {
      await customerServiceApi.reply({
        customer_telegram_id: selectedCustomer,
        content: inputText.trim(),
      });
      setInputText('');
      await fetchMessages(selectedCustomer);
      await fetchConversations();
      if (activeTab === 'strategy') await fetchAiLogs(selectedCustomer);
    } catch {
      message.error('发送失败');
    } finally {
      setSending(false);
    }
  };

  const handleSendPhoto = async (file: File) => {
    if (!selectedCustomer) return;
    setSending(true);
    try {
      await customerServiceApi.replyPhoto(selectedCustomer, file);
      await fetchMessages(selectedCustomer);
      await fetchConversations();
    } catch {
      message.error('图片发送失败');
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleSendPhoto(file);
      e.target.value = '';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) handleSendPhoto(file);
        return;
      }
    }
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    return isToday ? time : `${d.getMonth() + 1}/${d.getDate()} ${time}`;
  };

  const getMediaUrl = (fileId: string, mediaAccessToken?: string | null) => {
    if (!mediaAccessToken) return '';
    const base = import.meta.env.VITE_API_URL || '/api/v1';
    return `${base}/customer-service/media/${encodeURIComponent(fileId)}?ticket=${encodeURIComponent(mediaAccessToken)}`;
  };

  const handleImageDoubleClick = (msg: Message) => {
    if (!msg.media_file_id || !msg.media_access_token) return;
    setPreviewSrc(getMediaUrl(msg.media_file_id, msg.media_access_token));
    setPreviewVisible(true);
  };

  const renderMessageContent = (msg: Message) => {
    const mediaUrl = msg.media_file_id
      ? getMediaUrl(msg.media_file_id, msg.media_access_token)
      : '';
    const hasMedia = Boolean(mediaUrl) && ['photo', 'video', 'sticker', 'document'].includes(msg.message_type);
    const isOut = msg.direction === 'out';
    return (
      <>
        {hasMedia && msg.message_type === 'photo' && (
          <img
            src={mediaUrl}
            alt="图片"
            onDoubleClick={() => handleImageDoubleClick(msg)}
            style={{
              maxWidth: '100%',
              maxHeight: 300,
              borderRadius: 6,
              display: 'block',
              cursor: 'pointer',
              marginBottom: msg.content && !['[图片]'].includes(msg.content) ? 6 : 0,
            }}
            title="双击放大"
            loading="lazy"
          />
        )}
        {hasMedia && msg.message_type === 'video' && (
          <video
            src={mediaUrl}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: 300,
              borderRadius: 6,
              display: 'block',
              marginBottom: msg.content && !['[视频]'].includes(msg.content) ? 6 : 0,
            }}
          />
        )}
        {hasMedia && msg.message_type === 'sticker' && (
          <img
            src={mediaUrl}
            alt="贴纸"
            onDoubleClick={() => handleImageDoubleClick(msg)}
            style={{ maxWidth: 150, maxHeight: 150, display: 'block', marginBottom: 2, cursor: 'pointer' }}
            title="双击放大"
            loading="lazy"
          />
        )}
        {hasMedia && msg.message_type === 'document' && (
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: isOut ? '#fff' : '#1890ff', textDecoration: 'underline' }}
          >
            {msg.content || '[文件]'}
          </a>
        )}
        {msg.media_file_id && !hasMedia && ['photo', 'video', 'sticker', 'document'].includes(msg.message_type) && (
          <div style={{ fontSize: 12, opacity: 0.8 }}>媒体访问凭证缺失或已过期，请等待列表刷新</div>
        )}
        {msg.message_type === 'text' && (
          <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.content}</div>
        )}
        {hasMedia && msg.message_type !== 'document' && msg.content && !['[图片]', '[视频]', '[贴纸]', '[媒体]'].includes(msg.content) && (
          <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', marginTop: 4 }}>{msg.content}</div>
        )}
        {!hasMedia && msg.message_type !== 'text' && (
          <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{msg.content}</div>
        )}
      </>
    );
  };

  const openCreateDocument = (type: 'qa' | 'article') => {
    setKbForm({
      ...DEFAULT_DOC_FORM,
      type,
    });
    setKbModalOpen(true);
  };

  const openEditDocument = (doc: KbDocument) => {
    setKbForm({
      id: doc.id,
      type: doc.type,
      title: doc.title,
      question: doc.question || '',
      answer: doc.answer || '',
      content: doc.content || '',
      tags: (doc.tags || []).join(', '),
      priority: doc.priority,
      is_active: doc.is_active,
    });
    setKbModalOpen(true);
  };

  const handleSaveDocument = async () => {
    if (!kbForm.title.trim()) {
      message.warning('请输入标题');
      return;
    }
    if (kbForm.type === 'qa' && (!kbForm.question.trim() || !kbForm.answer.trim())) {
      message.warning('Q&A 类型必须填写问题和答案');
      return;
    }
    if (kbForm.type === 'article' && !kbForm.content.trim()) {
      message.warning('说明文档必须填写内容');
      return;
    }

    const payload = {
      type: kbForm.type,
      title: kbForm.title.trim(),
      question: kbForm.question.trim() || undefined,
      answer: kbForm.answer.trim() || undefined,
      content: kbForm.content.trim() || undefined,
      tags: kbForm.tags.split(',').map((item) => item.trim()).filter(Boolean),
      priority: kbForm.priority,
      is_active: kbForm.is_active,
    };

    try {
      setKbSaving(true);
      if (kbForm.id) {
        await customerServiceApi.updateKbDocument(kbForm.id, payload as any);
        message.success('知识库文档已更新');
      } else {
        await customerServiceApi.createKbDocument(payload as any);
        message.success('知识库文档已创建');
      }
      setKbModalOpen(false);
      setKbForm(DEFAULT_DOC_FORM);
      await fetchKbDocuments();
    } catch {
      // 全局拦截器已提示
    } finally {
      setKbSaving(false);
    }
  };

  const handleToggleDocument = async (doc: KbDocument, checked: boolean) => {
    try {
      await customerServiceApi.toggleKbDocument(doc.id, checked);
      await fetchKbDocuments();
      message.success(checked ? '已启用' : '已停用');
    } catch {
      // 全局拦截器已提示
    }
  };

  const handleDeleteDocument = (doc: KbDocument) => {
    Modal.confirm({
      title: '确认删除知识库文档',
      content: `确定删除「${doc.title}」？删除后将不再参与 AI 检索。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await customerServiceApi.deleteKbDocument(doc.id);
        await fetchKbDocuments();
        message.success('知识库文档已删除');
      },
    });
  };

  const handleSaveStrategy = async () => {
    try {
      setSavingAiConfig(true);
      await serverControlApi.updateAiSupportConfig(aiConfig);
      await fetchAiConfig();
      message.success('AI 策略已保存');
    } catch {
      // 全局拦截器已提示
    } finally {
      setSavingAiConfig(false);
    }
  };

  const handlePreviewAi = async () => {
    if (!previewText.trim()) {
      message.warning('请输入测试问题');
      return;
    }
    try {
      setPreviewLoading(true);
      const data: any = await customerServiceApi.previewAiReply({
        message_text: previewText.trim(),
        customer_telegram_id: selectedCustomer || undefined,
      });
      setPreviewResult(data);
      await fetchAiLogs(selectedCustomer);
    } catch {
      // 全局拦截器已提示
    } finally {
      setPreviewLoading(false);
    }
  };

  const selectedConv = conversations.find((c) => c.customer_telegram_id === selectedCustomer);
  const showConvList = !isMobile || !selectedCustomer;
  const showChatArea = !isMobile || !!selectedCustomer;

  const convListPanel = (
    <div
      style={{
        width: isMobile ? '100%' : 300,
        borderRight: isMobile ? 'none' : '1px solid #f0f0f0',
        display: showConvList ? 'flex' : 'none',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 600, fontSize: 16 }}>
        客服消息
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loadingConv && conversations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : conversations.length === 0 ? (
          <Empty description="暂无会话" style={{ marginTop: 60 }} />
        ) : (
          conversations.map((conv) => (
            <div
              key={conv.customer_telegram_id}
              onClick={() => handleSelectConversation(conv.customer_telegram_id)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid #fafafa',
                background: selectedCustomer === conv.customer_telegram_id ? '#e6f7ff' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    marginRight: 8,
                  }}
                >
                  {conv.customer_name}
                </span>
                <span style={{ fontSize: 12, color: '#999', flexShrink: 0 }}>
                  {formatTime(conv.last_time)}
                </span>
              </div>
              {conv.customer_username && (
                <div style={{ fontSize: 12, color: '#1890ff', marginTop: 2 }}>{conv.customer_username}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <span
                  style={{
                    fontSize: 13,
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    marginRight: 8,
                  }}
                >
                  {conv.last_message}
                </span>
                {conv.unread_count > 0 && (
                  <Badge count={conv.unread_count} size="small" />
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const chatAreaPanel = (
    <div style={{ flex: 1, display: showChatArea ? 'flex' : 'none', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
        {isMobile && selectedCustomer && (
          <LeftOutlined onClick={handleBack} style={{ cursor: 'pointer', fontSize: 16 }} />
        )}
        {selectedConv ? (
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedConv.customer_name}
            {selectedConv.customer_username && (
              <span style={{ color: '#1890ff', marginLeft: 8, fontWeight: 400 }}>{selectedConv.customer_username}</span>
            )}
          </div>
        ) : '选择一个会话'}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '16px 20px', background: '#f5f5f5' }}>
        {!selectedCustomer ? (
          <Empty description="请从左侧选择会话" style={{ marginTop: 80 }} />
        ) : loadingMsg && messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : messages.length === 0 ? (
          <Empty description="暂无消息" style={{ marginTop: 80 }} />
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                justifyContent: msg.direction === 'out' ? 'flex-end' : 'flex-start',
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  maxWidth: isMobile ? '85%' : '70%',
                  padding: isMobile ? '6px 10px' : '8px 14px',
                  borderRadius: msg.direction === 'out' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: msg.direction === 'out' ? '#1890ff' : '#fff',
                  color: msg.direction === 'out' ? '#fff' : '#333',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  wordBreak: 'break-word',
                }}
              >
                {renderMessageContent(msg)}
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 4,
                    textAlign: 'right',
                    color: msg.direction === 'out' ? 'rgba(255,255,255,0.7)' : '#999',
                  }}
                >
                  {formatTime(msg.created_at)}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {selectedCustomer && (
        <div style={{ padding: isMobile ? '8px 12px' : '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <Button
            icon={<PictureOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            size="middle"
            title="发送图片"
          />
          <TextArea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={isMobile ? '输入消息...' : '输入消息... (Enter 发送，Shift+Enter 换行，Ctrl+V 粘贴图片)'}
            autoSize={{ minRows: 1, maxRows: 4 }}
            style={{ flex: 1 }}
            disabled={sending}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={sending}
            disabled={!inputText.trim()}
          >
            {isMobile ? '' : '发送'}
          </Button>
        </div>
      )}
    </div>
  );

  const knowledgeColumns = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (value: 'qa' | 'article') => (
        <Tag color={value === 'qa' ? 'blue' : 'purple'}>{value === 'qa' ? 'Q&A' : '说明文档'}</Tag>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (_: any, record: KbDocument) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.title}</div>
          {!!record.tags?.length && (
            <div style={{ marginTop: 4 }}>
              {record.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (value: boolean) => (value ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
    },
    {
      title: '切片数',
      dataIndex: 'chunk_count',
      key: 'chunk_count',
      width: 90,
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      render: (_: any, record: KbDocument) => (
        <Space>
          <Button size="small" onClick={() => openEditDocument(record)}>编辑</Button>
          <Switch size="small" checked={record.is_active} onChange={(checked) => handleToggleDocument(record, checked)} />
          <Button size="small" danger onClick={() => handleDeleteDocument(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  const aiLogColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (value: string | null) => formatTime(value),
    },
    {
      title: '决策',
      dataIndex: 'decision',
      key: 'decision',
      width: 90,
      render: (value: string) => {
        const colorMap: Record<string, string> = {
          auto: 'success',
          handoff: 'orange',
          preview: 'blue',
          error: 'red',
        };
        return <Tag color={colorMap[value] || 'default'}>{value}</Tag>;
      },
    },
    {
      title: '意图/风险',
      key: 'intent_risk',
      width: 180,
      render: (_: any, record: AiReplyLog) => (
        <div>
          <div>{record.intent || '-'}</div>
          {record.risk_level && <Tag style={{ marginTop: 4 }}>{record.risk_level}</Tag>}
        </div>
      ),
    },
    {
      title: '用户问题',
      dataIndex: 'request_text',
      key: 'request_text',
      ellipsis: true,
    },
    {
      title: '回复/原因',
      key: 'reply',
      render: (_: any, record: AiReplyLog) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {record.reply_text || record.handoff_reason || '-'}
        </div>
      ),
    },
  ];

  const previewDocTitles = (previewResult?.matched_document_ids || [])
    .map((id) => kbDocuments.find((doc) => doc.id === id)?.title)
    .filter(Boolean);

  return (
    <>
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: previewVisible,
          src: previewSrc,
          onVisibleChange: (v) => setPreviewVisible(v),
        }}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />

      <Modal
        title={kbForm.id ? '编辑知识库文档' : '新增知识库文档'}
        open={kbModalOpen}
        onCancel={() => setKbModalOpen(false)}
        onOk={handleSaveDocument}
        confirmLoading={kbSaving}
        destroyOnClose
        width={720}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 8 }}>类型</div>
            <Select
              style={{ width: '100%' }}
              value={kbForm.type}
              onChange={(value) => setKbForm((prev) => ({ ...prev, type: value }))}
              options={[
                { label: 'Q&A', value: 'qa' },
                { label: '说明文档', value: 'article' },
              ]}
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>标题</div>
            <Input
              value={kbForm.title}
              onChange={(e) => setKbForm((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>
          {kbForm.type === 'qa' ? (
            <>
              <div>
                <div style={{ marginBottom: 8 }}>问题</div>
                <TextArea
                  value={kbForm.question}
                  onChange={(e) => setKbForm((prev) => ({ ...prev, question: e.target.value }))}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </div>
              <div>
                <div style={{ marginBottom: 8 }}>答案</div>
                <TextArea
                  value={kbForm.answer}
                  onChange={(e) => setKbForm((prev) => ({ ...prev, answer: e.target.value }))}
                  autoSize={{ minRows: 4, maxRows: 8 }}
                />
              </div>
            </>
          ) : (
            <div>
              <div style={{ marginBottom: 8 }}>说明内容</div>
              <TextArea
                value={kbForm.content}
                onChange={(e) => setKbForm((prev) => ({ ...prev, content: e.target.value }))}
                autoSize={{ minRows: 8, maxRows: 16 }}
              />
            </div>
          )}
          <div>
            <div style={{ marginBottom: 8 }}>标签（英文逗号分隔）</div>
            <Input
              value={kbForm.tags}
              onChange={(e) => setKbForm((prev) => ({ ...prev, tags: e.target.value }))}
              placeholder="入驻, 机器人, 支付"
            />
          </div>
          <Space align="center">
            <div>
              <div style={{ marginBottom: 8 }}>优先级</div>
              <InputNumber
                min={0}
                max={100}
                value={kbForm.priority}
                onChange={(val) => setKbForm((prev) => ({ ...prev, priority: Number(val || 0) }))}
              />
            </div>
            <div>
              <div style={{ marginBottom: 8 }}>启用状态</div>
              <Switch
                checked={kbForm.is_active}
                onChange={(checked) => setKbForm((prev) => ({ ...prev, is_active: checked }))}
              />
            </div>
          </Space>
        </Space>
      </Modal>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'conversations',
            label: '会话',
            children: (
              <div style={{ display: 'flex', height: 'calc(100vh - 180px)', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                {convListPanel}
                {chatAreaPanel}
              </div>
            ),
          },
          {
            key: 'knowledge',
            label: '知识库',
            children: (
              <div style={{ background: '#fff', borderRadius: 8, padding: 16 }}>
                <Space style={{ marginBottom: 16 }}>
                  <Button icon={<PlusOutlined />} type="primary" onClick={() => openCreateDocument('qa')}>
                    新增 Q&A
                  </Button>
                  <Button icon={<PlusOutlined />} onClick={() => openCreateDocument('article')}>
                    新增说明文档
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  loading={loadingKb}
                  columns={knowledgeColumns}
                  dataSource={kbDocuments}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 900 }}
                />
              </div>
            ),
          },
          {
            key: 'strategy',
            label: 'AI 策略',
            children: (
              <div style={{ background: '#fff', borderRadius: 8, padding: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <Alert
                    type="info"
                    showIcon
                    message="这里只维护低风险自动回复策略和 AI 预览。OpenAI API Key 与模型配置请到系统设置页面修改。"
                  />

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 12 }}>自动回复策略</div>
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div>
                          <div style={{ marginBottom: 8 }}>AI 总开关</div>
                          <Switch
                            checked={aiConfig.enabled}
                            onChange={(checked) => setAiConfig((prev) => ({ ...prev, enabled: checked }))}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 8 }}>低风险自动回复</div>
                          <Switch
                            checked={aiConfig.low_risk_auto_reply_enabled}
                            onChange={(checked) => setAiConfig((prev) => ({ ...prev, low_risk_auto_reply_enabled: checked }))}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 8 }}>允许自动回复的意图</div>
                          <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            value={aiConfig.allowed_auto_reply_intents}
                            onChange={(value) => setAiConfig((prev) => ({ ...prev, allowed_auto_reply_intents: value }))}
                            options={INTENT_OPTIONS.map((item) => ({ label: item, value: item }))}
                          />
                        </div>
                        <div>
                          <div style={{ marginBottom: 8 }}>强制转人工关键词（英文逗号分隔）</div>
                          <TextArea
                            value={(aiConfig.force_handoff_keywords || []).join(', ')}
                            onChange={(e) => setAiConfig((prev) => ({
                              ...prev,
                              force_handoff_keywords: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                            }))}
                            autoSize={{ minRows: 3, maxRows: 6 }}
                          />
                        </div>
                        <Button
                          type="primary"
                          onClick={handleSaveStrategy}
                          loading={savingAiConfig || loadingAiConfig}
                        >
                          保存策略
                        </Button>
                      </Space>
                    </div>

                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 12 }}>AI 回复预览</div>
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div>
                          <div style={{ marginBottom: 8 }}>测试问题</div>
                          <TextArea
                            value={previewText}
                            onChange={(e) => setPreviewText(e.target.value)}
                            placeholder="输入一个常见商户问题，例如：机器人怎么绑定？"
                            autoSize={{ minRows: 4, maxRows: 8 }}
                          />
                        </div>
                        <div>
                          {selectedCustomer
                            ? `当前将带入会话上下文：${selectedConv?.customer_name || selectedCustomer}`
                            : '当前未选择会话，将只使用测试问题本身做预览。'}
                        </div>
                        <Button
                          icon={<RobotOutlined />}
                          onClick={handlePreviewAi}
                          loading={previewLoading}
                        >
                          生成 AI 预览
                        </Button>
                        {previewResult && (
                          <div style={{ background: '#fafafa', borderRadius: 8, padding: 12, border: '1px solid #f0f0f0' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Tag color="blue">{previewResult.intent}</Tag>
                                <Tag color={previewResult.risk_level === 'low' ? 'success' : previewResult.risk_level === 'medium' ? 'orange' : 'red'}>
                                  {previewResult.risk_level}
                                </Tag>
                                <Tag>{previewResult.can_answer_from_kb ? '可答复' : '不可自动答复'}</Tag>
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {previewResult.reply_text || previewResult.handoff_reason || '无返回内容'}
                              </div>
                              {!!previewDocTitles.length && (
                                <div>命中文档：{previewDocTitles.join('、')}</div>
                              )}
                            </Space>
                          </div>
                        )}
                      </Space>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>最近 AI 决策日志</div>
                    <Table
                      rowKey="id"
                      loading={loadingAiLogs}
                      columns={aiLogColumns}
                      dataSource={aiLogs}
                      pagination={{ pageSize: 8 }}
                      scroll={{ x: 1000 }}
                    />
                  </div>
                </Space>
              </div>
            ),
          },
        ]}
      />
    </>
  );
};

export default CustomerService;
